import { useMemo, useRef } from "react";
import { func, node, string } from "prop-types";
import { applyCommentStorePatch } from "@repo/structure";
import { useIdleTimer } from "react-idle-timer";
import { Manager } from "socket.io-client";
import socketParser from "socket.io-msgpack-parser";
import {
  CoreCommands,
  ProjectEditorCommands,
  EditorSocketContext,
} from "@client";
import {
  createProjectVersionComment,
  editProjectVersionComment,
  moveProjectVersionComment,
  removeProjectVersionComment,
  removeProjectVersionCommentByPath,
  renameProjectVersionCommentElementQuery, startRecordingSessionQuery
} from "@query";
import { useStores } from "@hooks";
import {
  useEstimateEditorStore,
  useEstimateComments,
  DEFAULT_INACTIVE_PERIOD,
  DEFAULT_INACTIVE_THROTTLE,
  SessionRecorderWorker,
} from "@tools"
import { PolicyDialog } from "@dialogs";

export const ProposalSocketProvider = ({
  children,
  commentHash,
  // reloadProposalComments,
  shareKey,
  clientEmail,
}) => {
  const { userStore } = useStores();
  const editorStore = useEstimateEditorStore();
  const commentStore = useEstimateComments();
  
  const socket = useRef(null);
  const manager = useRef(null);
  
  const lastSentAction = useRef(null);
  const setupComplete = useRef(false);
  const connectionOnline = useRef(false);
  const policyAccepted = useRef(false);
  const sessionStartRequest = useRef(false);
  
  const sessionRecorder = useMemo(() => (
    new SessionRecorderWorker()
  ), []);
  
  const handlePolicyAccept = () => {
    policyAccepted.current = true;
  }
  
  const saveAndSendAction = (command, payload) => {
    if (!socket.current?.connected)
      return;
    
    if (editorStore.isArchived && command !== ProjectEditorCommands.VERSION_REQUEST)
      return;
    
    const timestamp = new Date().getTime();
    payload.timestamp = timestamp;
    payload.projectUuid = commentHash;
    payload.projectHash = commentHash;
    lastSentAction.current = { command, payload, timestamp };
    
    socket.current.emit(command, payload);
  };
  
  const setupProjectEvents = (on) => {
    if(commentHash)
      socket.current[on ? "on" : "off"](ProjectEditorCommands.COMMENT_PATCH, onCommentPatch);
  }
  
  const joinProposal = async () => {
    setupProjectEvents(true);
    socket.current.emit(CoreCommands.JOIN_PROPOSAL, {
      commentHash,
      joinEdition: false,
      joinComments: Boolean(commentHash)
    });
    connectionOnline.current = true;
  }
  
  const initSocket = async () => {
    manager.current = new Manager(import.meta.env.APP_COLLAB_SERVICE, {
      path: import.meta.env.APP_COLLAB_SERVICE_WS_PATH,
      host: import.meta.env.APP_COLLAB_SERVICE_WS_HOST,
      transports: ["websocket"],
      parser: socketParser,
    });
    socket.current = manager.current.socket("/");
    
    socket.current.on(CoreCommands.CONNECTION, onConnect);
    socket.current.on(CoreCommands.CONNECT_ERROR, onDisconnect);
    socket.current.on(CoreCommands.DISCONNECT, onDisconnect);
    
    await joinProposal();
  };
  
  const { start, resume } = useIdleTimer({
    
    onIdle: () => {
      console.log("Closing idle session")
      if(connectionOnline.current) {
        setupProjectEvents(false);
        socket.current.emit(CoreCommands.CLOSE_PROPOSAL, {
          commentHash,
          joinEdition: false,
          joinComments: Boolean(commentHash)
        });
        
        lastSentAction.current = "END";
      }
      
      sessionRecorder.endRecording();
      // connectionOnline.current = false;
      sessionStartRequest.current = false;
    },
    
    onActive: async (data, idleTimer) => {
      if(!idleTimer.isIdle())
        return;
      if(setupComplete.current && connectionOnline.current) {
        await joinProposal();
        if(policyAccepted.current)
          await requestProposalSessionStart();
      }
    },
    
    onAction: (data, idleTimer) => {
      if(idleTimer.isIdle())
        return;
      if(!sessionStartRequest.current && connectionOnline.current && policyAccepted.current)
        requestProposalSessionStart();
    },
    startManually: true,
    timeout: DEFAULT_INACTIVE_PERIOD,
    throttle: DEFAULT_INACTIVE_THROTTLE,
    events: [
      'mousemove',
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove',
    ]
  });
  
  /** --------------------------- CONNECTIONS --------------------------- */
  
  const onConnect = async () => {
    if(setupComplete.current && !connectionOnline.current) {
      console.log("PROPOSAL RECONNECTED");
      await joinProposal();
      resume();
    } else if(!setupComplete.current) {
      console.log("PROPOSAL CONNECTED");
      setupComplete.current = true;
      start();
    }
  };
  
  const onDisconnect = () => {
    console.log("PROPOSAL DISCONNECTED");
    connectionOnline.current = false;
    sessionStartRequest.current = false;
    if(sessionRecorder.recorderStarted)
      sessionRecorder.endRecording();
  };
  
  /** --------------------------- SESSION START --------------------------- */
  
  const requestProposalSessionStart = async () => {
    try {
      sessionStartRequest.current = true;
      console.log("Starting new session");
      const sessionId = await startRecordingSessionQuery(shareKey, clientEmail);
      saveAndSendAction(CoreCommands.PROPOSAL_SESSION_START, { sessionId });
      await sessionRecorder?.startRecording(sessionId);
    } catch(e) {
      sessionStartRequest.current = false;
    }
  }
  
  /** --------------------------- REMOTE ACTIONS --------------------------- */
  
  const onCommentPatch = (data) => {
    try {
      commentStore.historyManager.withoutUndo(() => {
        applyCommentStorePatch(commentStore, data.patches);
      });
    } catch(e) {
      console.log("Error: onCommentPatch", e);
    }
  }
  
  /** --------------------------- USER ACTIONS --------------------------- */

  const requestCommentPatch = (patches) => {
    saveAndSendAction(ProjectEditorCommands.COMMENT_PATCH, {
      versionKey: editorStore.currentVersionKey,
      timestamp: new Date().getTime(),
      patches,
    });
  };
  
  const requestCommentAdd = async (commenterData, path, name, body, mentions, internal, parentUuid) => {
    const versionKey = editorStore.currentVersionKey;
    const commentData = await createProjectVersionComment(
      commentHash,
      commenterData.uuid, versionKey, path, name, body, mentions, internal, parentUuid
    );
    return {
      uuid: commentData.uuid,
      parentUuid,
      body,
      createdAt: commentData.createdAt,
      user: commenterData,
      isRead: true,
    }
  };
  
  const requestCommentEdit = async (commentUuid, commenterUuid, body, mentions) => {
    await editProjectVersionComment(commentUuid, commentHash, commenterUuid, body, mentions);
  };
  
  const requestCommentRemove = async (commentUuid, userUuid) => {
    const versionKey = editorStore.currentVersionKey;
    await removeProjectVersionComment(
      commentUuid, commentHash, userUuid, versionKey
    );
  };
  
  const requestCommentPathRemove = async (path) => {
    const versionKey = editorStore.currentVersionKey;
    commentStore.removeCommentByPath(path);
    await removeProjectVersionCommentByPath(
      commentHash,
      userStore.data.uuid,
      versionKey, path,
    );
  };
  
  const requestCommentMove = async (oldPath, path) => {
    const versionKey = editorStore.currentVersionKey;
    commentStore.updateCommentPath(oldPath, path);
    await moveProjectVersionComment(
      commentHash,
      userStore.data.uuid,
      versionKey,
      oldPath,
      path,
    );
  };
  
  const requestCommentPathElementRename = async (path, taskName) => {
    const versionKey = editorStore.currentVersionKey;
    commentStore.updateCommentElementName(path, taskName);
    await renameProjectVersionCommentElementQuery(
      commentHash,
      userStore.data.uuid,
      versionKey,
      path,
      taskName,
    );
  };

  const value = {
    initSocket,
    
    requestCommentPatch,
    requestCommentAdd,
    requestCommentEdit,
    requestCommentRemove,
    requestCommentPathRemove,
    requestCommentMove,
    requestCommentPathElementRename,
  };

  return (
    <EditorSocketContext.Provider value={value}>
      {children}
      <PolicyDialog onDisclaimerClose={handlePolicyAccept} />
    </EditorSocketContext.Provider>
  );
};

ProposalSocketProvider.propTypes = {
  children: node.isRequired,
  commentHash: string.isRequired,
  reloadProposalComments: func.isRequired,
  shareKey: string.isRequired,
  clientEmail: string,
}
